import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import type { Database } from '@/types/database';
import type { Lead, LeadStage } from './types';

type LeadInsert = Database['public']['Tables']['leads']['Insert'];
type LeadUpdate = Database['public']['Tables']['leads']['Update'];

/** Todos os leads vivos do funil comercial. */
export function useLeads() {
  return useQuery({
    queryKey: ['leads'],
    staleTime: 30_000,
    queryFn: async (): Promise<Lead[]> => {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .is('deleted_at', null)
        .order('updated_at', { ascending: false })
        .limit(1000);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface CreateLeadInput {
  name: string;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  stage?: LeadStage;
  estimated_value?: number | null;
  source?: string | null;
  notes?: string | null;
  assigned_to_user_id?: string | null;
}

export function useCreateLead() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateLeadInput): Promise<Lead> => {
      const insert: LeadInsert = {
        name: input.name,
        contact_name: input.contact_name ?? null,
        phone: input.phone ?? null,
        email: input.email ?? null,
        stage: input.stage ?? 'new',
        estimated_value: input.estimated_value ?? null,
        source: input.source ?? null,
        notes: input.notes ?? null,
        assigned_to_user_id: input.assigned_to_user_id ?? null,
        created_by_user_id: user?.id ?? null,
      };
      const { data, error } = await supabase.from('leads').insert(insert).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead criado!');
    },
    onError: (e: Error) => toast.error('Erro ao criar lead: ' + e.message),
  });
}

export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: LeadUpdate }) => {
      const { error } = await supabase.from('leads').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (e: Error) => toast.error('Erro ao atualizar lead: ' + e.message),
  });
}

/**
 * Move um lead para outro estágio. Quando vai para `lost`, exige motivo;
 * ao sair de `lost`, limpa o motivo antigo.
 */
export function useMoveLeadStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      stage,
      lostReason,
    }: {
      id: string;
      stage: LeadStage;
      lostReason?: string | null;
    }) => {
      const updates: LeadUpdate = { stage };
      if (stage === 'lost') updates.lost_reason = lostReason ?? null;
      else updates.lost_reason = null;
      const { error } = await supabase.from('leads').update(updates).eq('id', id);
      if (error) throw error;
    },
    // Atualização otimista: o card muda de coluna na hora.
    onMutate: async ({ id, stage }) => {
      await qc.cancelQueries({ queryKey: ['leads'] });
      const previous = qc.getQueryData<Lead[]>(['leads']);
      if (previous) {
        qc.setQueryData<Lead[]>(
          ['leads'],
          previous.map((l) => (l.id === id ? { ...l, stage } : l)),
        );
      }
      return { previous };
    },
    onError: (e: Error, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['leads'], ctx.previous);
      toast.error('Erro ao mover lead: ' + e.message);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('leads')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead excluído');
    },
    onError: (e: Error) => toast.error('Erro ao excluir lead: ' + e.message),
  });
}
